
import { memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import { Layers, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNodeSettings } from '../NodeSettingsContext';
import { type StateNodeData } from '@/types/workflow-graph';
import { cn } from '@/lib/utils';

interface ParallelNodeProps {
    data: StateNodeData;
    selected?: boolean;
}

export const ParallelNode = memo(function ParallelNode({ data, selected }: ParallelNodeProps) {
    const { openSettings } = useNodeSettings();
    const { label, state, layoutDirection = 'LR' } = data;
    const branchCount = state.Branches?.length || 0;

    // Determine handle positions based on layout direction
    const isHorizontal = layoutDirection === 'LR';
    const inputPosition = isHorizontal ? Position.Left : Position.Top;
    const outputPosition = isHorizontal ? Position.Right : Position.Bottom;

    return (
        <div
            className={cn(
                'relative min-h-[100px] min-w-[260px] rounded-lg border-2 border-gray-700 bg-gray-50',
                'shadow-md',
                selected && 'ring-2 ring-blue-500 ring-offset-2'
            )}
        >
            {/* Input Handle - position based on layout direction */}
            <Handle
                type="target"
                id="target"
                position={inputPosition}
                className="!h-3 !w-3 !border-gray-900 !bg-gray-800"
            />

            {/* Header */}
            <div className="flex items-center gap-2 rounded-t-lg border-b border-gray-300 bg-gray-200 px-3 py-2">
                <Layers className="h-4 w-4 text-gray-900" />
                <span className="text-sm font-medium text-slate-900">{label}</span>
                <span className="ml-auto rounded-full bg-gray-700 px-2 py-0.5 text-xs text-white">
                    {branchCount} branches
                </span>
                <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 rounded-full text-slate-400 hover:bg-gray-300 hover:text-slate-600"
                    title="Node Settings"
                    onClick={(e) => {
                        e.stopPropagation();
                        openSettings(data);
                    }}
                >
                    <Settings size={12} />
                </Button>
            </div>

            {/* Content area */}
            <div className="p-3 text-xs text-slate-500">
                <p>Parallel execution: Fan-out / Fan-in</p>
                {state.Comment && <p className="mt-1 italic">{state.Comment}</p>}
            </div>

            {/* Output Handle - position based on layout direction */}
            <Handle
                type="source"
                id="source"
                position={outputPosition}
                className="!h-3 !w-3 !border-gray-900 !bg-gray-800"
            />
        </div>
    );
});
